<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppLayout from '@/components/layout/AppLayout.vue';
import CurriculaNav from '@/components/layout/CurriculaNav.vue';
import { Button } from '@/components/ui/button';

interface Deck {
  id: string;
  name: string;
  isDefault: boolean;
}

interface Card {
  id: string;
  shortId: string;
  question: string;
  answer: string;
  goal: string | null;
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const deck = ref<Deck | null>(null);
const cards = ref<Card[]>([]);
const loading = ref(false);
const error = ref('');

// Add-card form (the manual / "euphemism" path)
const question = ref('');
const answer = ref('');
const goal = ref('');
const adding = ref(false);
const addError = ref('');

async function fetchData() {
  if (!user.value) return;
  const id = route.params.id as string;
  loading.value = true;
  error.value = '';
  try {
    const [d, c] = await Promise.all([
      trpc.getDeck.query({ id }),
      trpc.listFlashcards.query({ deckId: id, limit: 500 }) as Promise<Card[]>,
    ]);
    deck.value = d;
    cards.value = c;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load deck';
  } finally {
    loading.value = false;
  }
}

async function addCard() {
  const q = question.value.trim();
  const a = answer.value.trim();
  if (!q || !a || adding.value || !deck.value) return;
  adding.value = true;
  addError.value = '';
  try {
    const card = await trpc.createCard.mutate({
      deckId: deck.value.id,
      question: q,
      answer: a,
      goal: goal.value.trim() || undefined,
    });
    // Newest on top, matching the form's position.
    cards.value.unshift(card as Card);
    question.value = '';
    answer.value = '';
    goal.value = '';
  } catch (e) {
    addError.value = e instanceof Error ? e.message : 'Failed to add card';
  } finally {
    adding.value = false;
  }
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
    return;
  }
  fetchData();
});

watch(user, (newUser) => {
  if (newUser) fetchData();
});

watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) fetchData();
  }
);
</script>

<template>
  <AppLayout>
    <template #nav>
      <CurriculaNav />
    </template>
    <div class="flex-1 overflow-y-auto scrollbar-thin">
      <div class="max-w-4xl mx-auto px-6 py-8">
        <div
          v-if="isLoading || loading"
          class="text-center text-muted-foreground"
        >
          Loading...
        </div>

        <div
          v-else-if="error"
          class="space-y-4"
        >
          <div class="bg-destructive/10 text-destructive p-4 rounded-md">
            {{ error }}
          </div>
          <Button
            variant="ghost"
            @click="router.push('/decks')"
          >
            Back to Decks
          </Button>
        </div>

        <div
          v-else-if="deck"
          class="space-y-6"
        >
          <div>
            <button
              class="text-sm text-muted-foreground hover:text-foreground mb-2"
              @click="router.push('/decks')"
            >
              &larr; Back to Decks
            </button>
            <h1 class="text-3xl font-bold">
              {{ deck.name }}
            </h1>
            <p class="text-muted-foreground mt-2 text-sm">
              {{ cards.length }} card{{ cards.length === 1 ? '' : 's' }}
            </p>
          </div>

          <!-- Add a card -->
          <div class="bg-card rounded-lg shadow-md p-6 border border-border space-y-3">
            <h2 class="text-lg font-medium">
              Add a card
            </h2>
            <form
              class="space-y-3"
              @submit.prevent="addCard"
            >
              <div class="space-y-1">
                <label class="text-xs uppercase tracking-wide text-muted-foreground">Question / prompt</label>
                <textarea
                  v-model="question"
                  rows="2"
                  placeholder="e.g., What word means a mild term substituted for a harsh one?"
                  class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                />
              </div>
              <div class="space-y-1">
                <label class="text-xs uppercase tracking-wide text-muted-foreground">Answer</label>
                <textarea
                  v-model="answer"
                  rows="2"
                  placeholder="e.g., Euphemism"
                  class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                />
              </div>
              <div class="space-y-1">
                <label class="text-xs uppercase tracking-wide text-muted-foreground">Goal <span class="normal-case">(optional)</span></label>
                <input
                  v-model="goal"
                  type="text"
                  placeholder="What this card is meant to retain"
                  class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
              </div>
              <div
                v-if="addError"
                class="bg-destructive/10 text-destructive p-2 rounded-md text-sm"
              >
                {{ addError }}
              </div>
              <div class="flex justify-end">
                <Button
                  type="submit"
                  :disabled="!question.trim() || !answer.trim() || adding"
                >
                  Add card
                </Button>
              </div>
            </form>
          </div>

          <!-- Cards -->
          <div
            v-if="cards.length === 0"
            class="bg-card rounded-lg border border-border p-8 text-center text-muted-foreground"
          >
            No cards yet. Add one above.
          </div>

          <div
            v-else
            class="space-y-2"
          >
            <div
              v-for="card in cards"
              :key="card.id"
              class="bg-card rounded-md border border-border p-3 text-sm space-y-1"
            >
              <div
                v-if="card.goal"
                class="text-xs uppercase tracking-wide text-muted-foreground"
              >
                Goal: <span class="normal-case tracking-normal italic text-foreground">{{ card.goal }}</span>
              </div>
              <div class="font-medium">
                {{ card.question }}
              </div>
              <div class="text-muted-foreground whitespace-pre-wrap">
                {{ card.answer }}
              </div>
              <div class="text-[10px] uppercase tracking-wide text-muted-foreground/60 pt-1 font-mono">
                {{ card.shortId }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
